import { useEffect, useState } from 'react';

type ThemeSource = 'dark' | 'light' | 'system' | null;
type EffectiveTheme = 'dark' | 'light';

interface ThemeState {
  source: ThemeSource;
  effective: EffectiveTheme;
}

function getEffectiveTheme(source: ThemeSource): EffectiveTheme {
  if (source === 'dark') return 'dark';
  if (source === 'light') return 'light';
  if (source === 'system' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return 'light';
}

function applyTheme(effective: EffectiveTheme): void {
  if (effective === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

export const useTheme = (): ThemeState => {
  const [theme, setTheme] = useState<ThemeState>(() => {
    const source =
      (document.documentElement.getAttribute(
        'data-theme-source'
      ) as ThemeSource) || null;
    const effective = getEffectiveTheme(source);
    return { source, effective };
  });

  useEffect(() => {
    if (theme.source) {
      applyTheme(theme.effective);
    }
  }, [theme.source, theme.effective]);

  useEffect(() => {
    if (theme.source !== 'system' || !window.matchMedia) {
      return () => {
        // no-op: explicit theme (dark,light) or matchMedia unavailable
      };
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      const newEffective: EffectiveTheme = e.matches ? 'dark' : 'light';
      applyTheme(newEffective);
      setTheme((prev) => ({ ...prev, effective: newEffective }));
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler);
    } else {
      mediaQuery.addListener(handler);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handler);
      } else {
        mediaQuery.removeListener(handler);
      }
    };
  }, [theme.source]);

  return theme;
};
